import { useCallback, useState } from 'react';
import { getWorkflow } from '@/services/workflowService';
import {
  emptyWorkflowState,
  type ScriptWorkflowState,
  type WorkflowStepKey,
  type WorkflowStepStatus,
} from '@/types/workflow';

export function useWorkflowSession() {
  const [workflow, setWorkflow] = useState<ScriptWorkflowState>(emptyWorkflowState());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyWorkflow = useCallback((state: ScriptWorkflowState) => {
    setWorkflow(state);
    setError(null);
  }, []);

  const resetToNew = useCallback(() => {
    setWorkflow(emptyWorkflowState());
    setError(null);
  }, []);

  const setStep = useCallback((step: WorkflowStepKey, status: WorkflowStepStatus) => {
    setWorkflow((prev) => ({
      ...prev,
      steps: { ...prev.steps, [step]: status },
    }));
  }, []);

  const refresh = useCallback(async (sessionId: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getWorkflow(sessionId);
      setWorkflow(data);
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to load workflow');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    workflow,
    loading,
    error,
    applyWorkflow,
    resetToNew,
    setStep,
    refresh,
    setWorkflow,
  };
}
